import { createEffect, createMemo, createSignal, For, Show, onCleanup, untrack } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { A } from '@solidjs/router';
import Text from '../components/Text';
import SellbotNav from '../components/SellbotNav';
import { useApi, isOk } from '../api';
import { useActiveBot } from '../state/activeBot';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { subscribeBotEngineEvents } from '../services/botEngineEvents';
import { formatTime } from '../services/dateFormat';
import type { SellbotConfigDto, SellbotSelfTestStepDto } from '../api';
import styles from './Sellbot.module.css';
import { firstError } from '../api/envelope';

const STEP_ORDER = [
    'config_consistency', 'home', 'special_chest_cleanup', 'ankauf_chest', 'verification_chest',
    'trash_chest', 'lager_chest', 'ankauf_lamp', 'quote_lamp', 'chamber_scan', 'price_sanity',
    'limits_sanity',
] as const;

type StepStatus = 'Pending' | 'Running' | 'Passed' | 'Failed' | 'Skipped' | 'Fixed';

interface StepResult {
    status: StepStatus;
    message: string | null;
    startedAt: string | null;
    finishedAt: string | null;
}

const IDLE_RESULT: StepResult = { status: 'Pending', message: null, startedAt: null, finishedAt: null };

function statusDotClass(status: StepStatus): string {
    switch (status) {
        case 'Passed': return styles.jdotOk;
        case 'Failed': return styles.jdotFailed;
        case 'Skipped': return styles.jdotSkip;
        default: return styles.jdotIntent;
    }
}

function statusIcon(status: StepStatus): string {
    switch (status) {
        case 'Passed': return 'fa-check';
        case 'Failed': return 'fa-xmark';
        case 'Fixed': return 'fa-wrench';
        case 'Running': return 'fa-spinner fa-spin';
        case 'Skipped': return 'fa-minus';
        default: return 'fa-hourglass-half';
    }
}

function statusBadgeClass(status: StepStatus): string {
    switch (status) {
        case 'Passed': return styles.badgeSuccess;
        case 'Failed': return styles.badgeDanger;
        case 'Fixed': return styles.badgeWarning;
        case 'Skipped': return styles.badgeMuted;
        default: return styles.badgeInfo;
    }
}

function statusResultClass(status: StepStatus): string {
    switch (status) {
        case 'Failed': return styles.selfTestResultFail;
        case 'Fixed': return styles.selfTestResultFixed;
        default: return styles.selfTestResultOk;
    }
}

function durationText(startedAt: string | null, finishedAt: string | null): string | null {
    if (!startedAt || !finishedAt) return null;
    const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

export default function SellbotSelfTest() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId } = useActiveBot();

    const [loading, setLoading] = createSignal(true);
    const [config, setConfig] = createSignal<SellbotConfigDto | null>(null);
    const [catalog, setCatalog] = createSignal<SellbotSelfTestStepDto[]>([]);
    const [running, setRunning] = createSignal(false);
    const [hasRun, setHasRun] = createSignal(false);
    const [lastRunAt, setLastRunAt] = createSignal<string | null>(null);
    const [results, setResults] = createStore<Record<string, StepResult>>({});

    const stepLabel = (step: string): string => {
        const key = 'SELLBOT_SELFTEST_STEP_' + step;
        const localized = t(key);
        return localized !== key ? localized : step;
    };

    const stepDescription = (step: string): string => {
        const key = 'SELLBOT_SELFTEST_STEP_DESC_' + step;
        const localized = t(key);
        return localized !== key ? localized : '';
    };

    const resetResults = (steps: SellbotSelfTestStepDto[]) => {
        const next: Record<string, StepResult> = {};
        for (const s of steps) next[s.key] = { ...IDLE_RESULT, status: s.configured ? 'Pending' : 'Skipped' };
        setResults(reconcile(next));
    };

    const load = async () => {
        const id = botId();
        setLoading(true);
        if (!id) { setConfig(null); setCatalog([]); setLoading(false); return; }

        const [cfgRes, stepsRes] = await Promise.all([api.sellbot.getConfig(id), api.sellbot.getSelfTestSteps(id)]);
        setConfig(isOk(cfgRes.data) ? cfgRes.data.value : null);
        const steps = isOk(stepsRes.data) ? (stepsRes.data.value?.steps ?? []) : [];
        setCatalog(steps);
        resetResults(steps);
        setLoading(false);
    };

    createEffect(() => {
        const id = botId();
        untrack(() => { void load(); });
        if (!id) return;

        const unsub = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type !== 'plugin_event' || evt.payload?.source !== 'sellbot' || evt.payload?.kind !== 'self_test_step') return;
            const { step, status, message, at } = evt.payload as { step: string; status: StepStatus; message: string | null; at: string };
            if (step === 'done') { setRunning(false); setLastRunAt(at); return; }
            setResults(step, (prev) => ({
                status,
                message: message ?? null,
                startedAt: status === 'Running' ? at : (prev?.startedAt ?? at),
                finishedAt: status === 'Running' ? null : at,
            }));
        });
        onCleanup(unsub);
    });

    const runSelfTest = async () => {
        const id = botId();
        if (!id) return;
        setRunning(true);
        setHasRun(true);
        resetResults(catalog());
        const res = await api.sellbot.runSelfTest(id);
        if (!isOk(res.data)) {
            setRunning(false);
            toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_FAILED'));
        }
    };

    const counts = createMemo(() => {
        const c = { passed: 0, failed: 0, fixed: 0, skipped: 0, pending: 0, total: STEP_ORDER.length };
        for (const step of STEP_ORDER) {
            const status = (results[step] ?? IDLE_RESULT).status;
            if (status === 'Passed') c.passed++;
            else if (status === 'Failed') c.failed++;
            else if (status === 'Fixed') c.fixed++;
            else if (status === 'Skipped') c.skipped++;
            else c.pending++;
        }
        return c;
    });

    return (
        <div class={styles.container}>
            <SellbotNav />
            <div class={styles.headerRow}>
                <div class="v" style={{ gap: '4px' }}>
                    <h1><Text val="SELLBOT_SELFTEST_TITLE" /></h1>
                    <span class={styles.heroSubtitle}><Text val="SELLBOT_SELFTEST_SUBTITLE" /></span>
                </div>
            </div>

            <Show when={botId()} fallback={
                <div class={styles.empty}><i class="fa-solid fa-robot" style={{ 'font-size': '3rem', opacity: 0.5 }} /><p><Text val="SELLBOT_NO_BOT" /></p></div>
            }>
                <Show when={!loading()} fallback={<div class={styles.skeletonGrid}><For each={[1, 2, 3]}>{() => <div class={styles.skeletonCard} />}</For></div>}>
                    <Show when={config()} fallback={
                        <div class={styles.empty}>
                            <i class="fa-solid fa-store-slash" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                            <p><Text val="SELLBOT_NOT_CONFIGURED" /></p>
                            <A href="/sellbot/config" class="btn btn-primary"><Text val="SELLBOT_SETTINGS_NAV" /></A>
                        </div>
                    }>
                        <Show when={!config()!.isActive} fallback={
                            <div class={styles.empty}>
                                <i class="fa-solid fa-triangle-exclamation" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                                <p><Text val="SELLBOT_SELFTEST_SHOP_MUST_BE_OFF" /></p>
                            </div>
                        }>
                            <div class="fcard v" style={{ gap: 'var(--spacing-lg)' }}>
                                <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)' }}>
                                    <button class="btn btn-primary" disabled={running()} onClick={() => void runSelfTest()}>
                                        <Show when={running()} fallback={<i class="fa-solid fa-play" />}>
                                            <i class="fa-solid fa-spinner fa-spin" />
                                        </Show>
                                        <span style={{ 'margin-left': '6px' }}>{t(running() ? 'SELLBOT_SELFTEST_RUNNING' : 'SELLBOT_SELFTEST_RUN')}</span>
                                    </button>

                                    <Show when={hasRun()}>
                                        <div class={styles.selfTestSummary}>
                                            <div class={styles.selfTestStat}>
                                                <span class={`${styles.selfTestStatValue} ${styles.selfTestStatOk}`}>{counts().passed}</span>
                                                <span class={styles.selfTestStatLabel}><Text val="SELLBOT_SELFTEST_STATUS_Passed" /></span>
                                            </div>
                                            <div class={styles.selfTestStat}>
                                                <span class={`${styles.selfTestStatValue} ${counts().failed > 0 ? styles.selfTestStatFail : ''}`}>{counts().failed}</span>
                                                <span class={styles.selfTestStatLabel}><Text val="SELLBOT_SELFTEST_STATUS_Failed" /></span>
                                            </div>
                                            <Show when={counts().fixed > 0}>
                                                <div class={styles.selfTestStat}>
                                                    <span class={`${styles.selfTestStatValue} ${styles.selfTestStatFixed}`}>{counts().fixed}</span>
                                                    <span class={styles.selfTestStatLabel}><Text val="SELLBOT_SELFTEST_STATUS_Fixed" /></span>
                                                </div>
                                            </Show>
                                            <div class={styles.selfTestStat}>
                                                <span class={styles.selfTestStatValue}>{counts().skipped}</span>
                                                <span class={styles.selfTestStatLabel}><Text val="SELLBOT_SELFTEST_STATUS_Skipped" /></span>
                                            </div>
                                            <Show when={lastRunAt() && !running()}>
                                                <div class={styles.selfTestStat}>
                                                    <span class={styles.selfTestStatValue} style={{ 'font-size': 'var(--font-size-base)' }}>{formatTime(lastRunAt()!)}</span>
                                                    <span class={styles.selfTestStatLabel}><Text val="SELLBOT_SELFTEST_LAST_RUN" /></span>
                                                </div>
                                            </Show>
                                        </div>
                                    </Show>
                                </div>

                                <div class={styles.selfTestList}>
                                    <For each={STEP_ORDER}>{(step) => {
                                        const result = () => results[step] ?? IDLE_RESULT;
                                        const duration = () => durationText(result().startedAt, result().finishedAt);
                                        return (
                                            <div
                                                class={`${styles.selfTestCard} ${result().status === 'Running' ? styles.selfTestCardRunning : ''}`}
                                                style={{ opacity: result().status === 'Skipped' ? 0.6 : 1 }}
                                            >
                                                <div class={styles.selfTestCardHead}>
                                                    <div class={styles.selfTestCardTitle}>
                                                        <span class={`${styles.tlDot} ${statusDotClass(result().status)}`}>
                                                            <i class={`fa-solid ${statusIcon(result().status)}`} />
                                                        </span>
                                                        <div class={styles.selfTestCardTitleText}>
                                                            <span class={styles.tlStep}>{stepLabel(step)}</span>
                                                            <Show when={stepDescription(step)}>
                                                                <span class={styles.selfTestDesc}>{stepDescription(step)}</span>
                                                            </Show>
                                                        </div>
                                                    </div>
                                                    <div class={styles.selfTestCardMeta}>
                                                        <span class={`${styles.badge} ${statusBadgeClass(result().status)}`}>
                                                            {t('SELLBOT_SELFTEST_STATUS_' + result().status)}
                                                        </span>
                                                        <Show when={duration()}>
                                                            <span class={styles.tlTime}>{duration()}</span>
                                                        </Show>
                                                    </div>
                                                </div>

                                                <Show when={result().status === 'Skipped'}>
                                                    <div class={styles.selfTestResultSkip}>
                                                        <span><Text val="SELLBOT_SELFTEST_NOT_CONFIGURED" /></span>
                                                        <A href="/sellbot/config" class="btn btn-sm btn-secondary"><Text val="SELLBOT_SELFTEST_CONFIGURE_NOW" /></A>
                                                    </div>
                                                </Show>
                                                <Show when={result().message}>
                                                    {(m) => (
                                                        <div class={`${styles.selfTestResult} ${statusResultClass(result().status)}`}>
                                                            {m()}
                                                        </div>
                                                    )}
                                                </Show>
                                            </div>
                                        );
                                    }}</For>
                                </div>
                            </div>
                        </Show>
                    </Show>
                </Show>
            </Show>
        </div>
    );
}
